<template>
  <div class="response-page">
    <!-- Header with balance only -->
    <div class="page-header">
      <div :class="['balance-text', balanceClass]">
        You have a balance of ₹{{ creditBalance }}
      </div>
    </div>

    <h1 class="page-title">Taxscan AI Response</h1>
    <p class="subheading">Below is the AI-generated summary based on your question.</p>

    <!-- AI response content -->
    <div class="response-container" ref="pdfContainer">
      <div v-html="responseHTML"></div>
    </div>

    <!-- Sources panel -->
    <div v-if="sources.length" class="sources-container">
      <h3>Sources</h3>
      <ul>
        <li v-for="(src, idx) in sources" :key="idx" class="source-item">
          <span class="source-number">{{ idx + 1 }}.</span>
          <a :href="src.url" target="_blank" rel="noopener">{{ src.title }}</a>
          <span v-if="src.citation"> — {{ src.citation }}</span>
        </li>
      </ul>
    </div>

    <!-- Actions -->
    <div class="button-group">
      <button @click="copyToClipboard" class="action-button secondary">📋 Copy</button>
      <button @click="downloadPDF" class="action-button secondary">📄 Download PDF</button>
      <button @click="askAgain" class="action-button primary">Ask Another Question</button>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import html2pdf from 'html2pdf.js/dist/html2pdf.min';
import { marked } from 'marked';

export default {
  name: 'ResponsePage',
  data() {
    return {
      responseHTML: '',
      sources: [],
      creditBalance: 0
    };
  },
  computed: {
    balanceClass() {
      if (this.creditBalance < 50) return 'red-balance';
      if (this.creditBalance < 150) return 'yellow-balance';
      return 'green-balance';
    }
  },
  async mounted() {
    // 1) Load AI response and sources
    this.loadResponse();
    this.loadSources();

    // 2) Deduct cost and fetch updated balance
    await this.deductAndLoadBalance();
  },
  methods: {
    loadResponse() {
      const raw = localStorage.getItem('ai_response') || '';
      this.responseHTML = marked.parse(raw);
    },
    loadSources() {
      try {
        this.sources = JSON.parse(localStorage.getItem('ai_sources') || '[]');
      } catch {
        this.sources = [];
      }
    },
    async deductAndLoadBalance() {
      // Read the cost stored by LoadingPage
      const cost = parseFloat(localStorage.getItem('total_cost') || '0');
      try {
        // Call backend to deduct and return new balance
        const resp = await api.post('/user/update-balance', { cost });
        this.creditBalance = resp.data.creditBalance;
        localStorage.setItem('credit_balance', String(this.creditBalance));
      } catch (err) {
        console.error('Failed to update balance:', err);
        // Fallback: show whatever was in localStorage
        this.creditBalance = parseFloat(localStorage.getItem('credit_balance') || '0');
      }
    },
    copyToClipboard() {
      const el = document.createElement('div');
      el.innerHTML = this.responseHTML;
      const text = el.textContent || el.innerText || '';
      navigator.clipboard.writeText(text).then(() => {
        alert('📋 Response copied to clipboard!');
      });
    },
    downloadPDF() {
      html2pdf().from(this.$refs.pdfContainer).save('AI_Response.pdf');
    },
    askAgain() {
      localStorage.removeItem('ai_response');
      localStorage.removeItem('ai_sources');
      localStorage.removeItem('total_cost');
      this.$router.push({ name: 'AskAI' });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

.response-page {
  padding: var(--space-md);
  max-width: var(--content-max-width, 1200px);
  margin: 0 auto;
  font-family: var(--font-sans, 'Poppins', sans-serif);
  text-align: center;
}

.page-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--space-lg);
}

.page-title {
  font-size: var(--font-size-xl, 1.75rem);
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.subheading {
  font-size: var(--font-size-base);
  color: var(--color-muted);
  margin-bottom: var(--space-lg);
}

.balance-text {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}
.red-balance { color: var(--color-danger); }
.yellow-balance { color: var(--color-warning); }
.green-balance { color: var(--color-success); }

.response-container {
  text-align: left;
  background: var(--color-bg);
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  font-size: var(--font-size-base);
  line-height: 1.6;
  margin-bottom: var(--space-lg);
}

.sources-container {
  text-align: left;
  background: var(--color-bg);
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  margin-bottom: var(--space-lg);
}

.button-group {
  display: flex;
  justify-content: center;
  gap: var(--space-md);
  flex-wrap: wrap;
  margin-bottom: var(--space-lg);
}

.action-button {
  font-family: inherit;
  font-weight: var(--font-weight-medium);
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}

.action-button.secondary {
  background: var(--color-accent-light, #bfdbfe);
  color: var(--color-accent-dark, #2563eb);
  border: 1px solid var(--color-accent);
}
.action-button.secondary:hover,
.action-button.secondary:focus {
  background-color: var(--color-accent, #3b82f6) !important;
  color: var(--color-on-accent, #ffffff) !important;
  border-color: var(--color-accent, #3b82f6) !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.action-button.primary {
  background: var(--color-success, #16a34a);
  color: #ffffff;
  padding: calc(var(--space-sm) * 1.25) var(--space-lg);
  border: none;
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}
.action-button.primary:hover,
.action-button.primary:focus {
  background: var(--color-success-dark, #15803d);
  transform: translateY(-2px);
  box-shadow: 0 8px 12px rgba(0,0,0,0.15);
}
</style>
